import React from "react";
import { ToolbarProps } from "react-big-calendar";
import { Button } from "react-bootstrap";

import classNames from "classnames";

import DownloadButton from "./DownloadButton";

import { selectSelectedTournament } from "../../../../features/tourneymgmt/tournamentManagementSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { AUTH_LEVELS } from "../../../../lib/globals";
import { capitalize, isChildTournament } from "../../../../lib/utils";
import { ICalendarEvent, ICalendarResource } from "../../../../types/shared";

const Toolbar = ({
    isUmpireSchedule = false,
    hasBgEvents = false,
    events = [],
    setShowConfirmModal,
    ...props
}: ToolbarProps<ICalendarEvent, ICalendarResource> & {
    isUmpireSchedule?: boolean;
    hasBgEvents?: boolean;
    events?: ICalendarEvent[];
    setShowConfirmModal?: React.Dispatch<
        React.SetStateAction<"confirm" | "unconfirm" | undefined>
    >;
}) => {
    let {
        localizer: { messages },
        label,
        view,
        onView,
        onNavigate,
        date,
    } = props;

    const user = useAppSelector(state => state.auth.user);
    const tournament = useAppSelector(selectSelectedTournament);
    const tournaments = useAppSelector(
        state => state.tournamentmanagement.tournaments,
    );

    const childTournaments = isChildTournament(tournament)
        ? undefined
        : tournaments.filter(t => t.parent === tournament?.id);

    return (
        <React.Fragment>
            <div className="rbc-toolbar">
                <div className="rbc-toolbar-left">
                    {view === "day" && (
                        <>
                            <span className="rbc-btn-group">
                                <button
                                    type="button"
                                    onClick={() => onNavigate("PREV")}
                                    className={
                                        tournament?.startDate &&
                                        date > new Date(tournament.startDate)
                                            ? ""
                                            : "hidden"
                                    }
                                >
                                    Prev
                                </button>
                            </span>
                            <span className="rbc-toolbar-label">{label}</span>
                            <span className="rbc-btn-group">
                                <button
                                    type="button"
                                    onClick={() => onNavigate("NEXT")}
                                    className={
                                        tournament?.endDate &&
                                        date < new Date(tournament.endDate)
                                            ? ""
                                            : "hidden"
                                    }
                                >
                                    Next
                                </button>
                            </span>
                        </>
                    )}
                </div>
                <div className="rbc-toolbar-right">
                    <span className="rbc-btn-group">
                        <button
                            type="button"
                            className={view === "day" ? "rbc-active" : ""}
                            onClick={() => onView("day")}
                        >
                            Day
                        </button>
                        <button
                            type="button"
                            className={view === "week" ? "rbc-active" : ""}
                            onClick={() => onView("week")}
                        >
                            Tournament Calendar
                        </button>
                    </span>
                </div>
                <div className="colorKey">
                    {childTournaments &&
                        childTournaments.map(ct => (
                            <div
                                key={ct.ageLevel}
                                className={
                                    `tournamentAge` + capitalize(ct.ageLevel)
                                }
                            >
                                <h4>{capitalize(ct.ageLevel)}</h4>
                            </div>
                        ))}
                    {isUmpireSchedule &&
                        hasBgEvents &&
                        !tournament?.settings?.umpireScheduleConfirmed && (
                            <Button
                                variant="primary"
                                className={classNames("scheduleConfirmButton")}
                                onClick={() =>
                                    setShowConfirmModal &&
                                    setShowConfirmModal("confirm")
                                }
                            >
                                Confirm Umpire Schedule
                            </Button>
                        )}
                    {isUmpireSchedule &&
                        hasBgEvents &&
                        tournament?.settings?.umpireScheduleConfirmed && (
                            <Button
                                variant="primary"
                                className={classNames("scheduleConfirmButton")}
                                onClick={() =>
                                    setShowConfirmModal &&
                                    setShowConfirmModal("unconfirm")
                                }
                            >
                                Unconfirm Umpire Schedule
                            </Button>
                        )}
                    {!isUmpireSchedule &&
                        !isChildTournament(tournament) &&
                        events.length > 0 && (
                            <DownloadButton
                                tournament={tournament}
                                events={events}
                            />
                        )}
                </div>
                {(isUmpireSchedule ||
                    (!isUmpireSchedule &&
                        user &&
                        AUTH_LEVELS.sitemanagement.includes(
                            user?.userLevel,
                        ))) && (
                    <h5 style={{ flexBasis: "100%" }}>
                        <span className={classNames("text-danger")}>
                            Note:{" "}
                        </span>
                        {isUmpireSchedule ? (
                            <span>Double-click a game to delete it. </span>
                        ) : (
                            <span>Click a game to delete it. </span>
                        )}
                        Click and drag a range of at least 1 hour to create a
                        game.
                    </h5>
                )}
            </div>
        </React.Fragment>
    );
};

export default Toolbar;
